import { Table, Tooltip, createStyles, useMantineTheme } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';
import { Text } from '~/domains/design-system/Text';

import type { FieldDescriptor, StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';
import useAlignStyle from '../TableModal/useAlignStyle';

import type { TableType } from './types';
import type { TableValue } from './types/common';
import { createValidationSchema } from './createValidationSchema';
import { TableRow } from './TableRow';

const useStyles = createStyles((theme) => ({
  headCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  note: {
    '& a': {
      color: theme.colorScheme === 'dark' ? theme.colors.splash[5] : theme.colors.splash[7],
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}));

function TableElement({
  allErrors,
  allValues,
  errors,
  field,
  isMetaHidden,
  isTouched,
  sectionKey,
  value,
  onValueChange,
}: StaticFieldProps<TableType>) {
  const theme = useMantineTheme();
  const { classes, cx } = useStyles();
  const { classes: alignClasses } = useAlignStyle();
  const isTableHeadHidden = !field.columns.some((column) => column.label);

  const handleValueChange = (rowKey, columnKey, newValue, skipTouched?: boolean) => {
    onValueChange(
      (prev) => ({
        ...(prev ?? {}),
        [rowKey]: {
          ...(prev?.[rowKey] ?? {}),
          [columnKey]: newValue,
        },
      }),
      skipTouched
    );
  };

  return (
    <div>
      {!isMetaHidden && <FieldMeta field={field} errors={errors} isTouched={isTouched} />}
      <Table data-test-id={dt.table.container}>
        {!isTableHeadHidden && (
          <thead>
            <tr>
              {field.columns.map((column) => (
                <th key={column.key}>
                  <div
                    className={cx(classes.headCell, {
                      [alignClasses.start]: column.params?.align === 'start',
                      [alignClasses.center]: column.params?.align === 'center',
                      [alignClasses.end]: column.params?.align === 'end',
                    })}
                    data-testi-id={dt.table.head.label}
                  >
                    <Text variant="subhead-small">{column.label}</Text>

                    {column.params?.tooltip ? (
                      <Tooltip label={column.params?.tooltip}>
                        <IconInfoCircle color={theme.colors.gray[7]} size={20} />
                      </Tooltip>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {field.data.map((row) => (
            <TableRow
              columns={field.columns}
              row={row}
              key={row.key}
              onChange={handleValueChange}
              value={value as TableValue}
              allErrors={allErrors}
              allValues={allValues}
              sectionKey={sectionKey}
            />
          ))}
        </tbody>
      </Table>
      {field.note && (
        <Text
          className={classes.note}
          dangerouslySetInnerHTML={{ __html: toSafeMarkup(field.note) }}
          variant={{
            base: 'body-small',
            sm: 'body-medium',
          }}
        />
      )}
    </div>
  );
}

const Descriptor: FieldDescriptor<TableType> = {
  FieldComponent: TableElement,
  name: 'Table',
  createValidationSchema,
};

export default Descriptor;

export type { TableType };
